"use client";

import React, { useEffect, useState } from "react";
import axios from "axios";

export default function SponsorLogosManager() {
  const CONFERENCES_URL = "https://eventscons.com/eventbackendnew/api/conferences/all";
  const LOGOS_BY_CONF_URL = (conferenceId) =>
    `https://eventscons.com/eventbackendnew/api/sponsors-logo/${conferenceId}`;

  const ADD_URL = "https://eventscons.com/eventbackendnew/api/sponsor-logo";
  const EDIT_URL = (id) => `https://eventscons.com/eventbackendnew/api/edit-sponsor-logo/${id}`;
  const DELETE_URL = (id) => `https://eventscons.com/eventbackendnew/api/sponsor-logo/${id}`;

  const USE_AUTH = false;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [conferences, setConferences] = useState([]);
  const [confLoading, setConfLoading] = useState(true);
  const [selectedConferenceId, setSelectedConferenceId] = useState("");

  const [err, setErr] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const [viewModal, setViewModal] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);

  const [addModal, setAddModal] = useState(false);
  const [adding, setAdding] = useState(false);

  const [editModal, setEditModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [addForm, setAddForm] = useState({
    conference_id: "",
    sponsor_title: "",
    sponsor_logo: null,
  });

  const [editForm, setEditForm] = useState({
    id: "",
    conference_id: "",
    sponsor_title: "",
    sponsor_logo: null,
    preview: "",
  });

  const getAuthToken = () =>
    typeof window === "undefined"
      ? null
      : localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("access_token");

  const apiHeaders = () => {
    const token = USE_AUTH ? getAuthToken() : null;
    return {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const normalizeArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.logos)) return data.logos;
    if (Array.isArray(data?.sponsors)) return data.sponsors;
    if (Array.isArray(data?.sponsor_logos)) return data.sponsor_logos;
    return [];
  };

  const getImageUrl = (item) => {
    return (
      item?.sponsor_logo_url ||
      item?.logo_url ||
      item?.image_url ||
      item?.sponsor_logo ||
      item?.logo ||
      ""
    );
  };

  const loadConferences = async () => {
    try {
      setConfLoading(true);
      setErr("");

      const res = await axios.get(CONFERENCES_URL, {
        headers: apiHeaders(),
      });

      const list = normalizeArray(res.data);
      setConferences(list);

      if (!selectedConferenceId && list.length > 0) {
        const firstId = String(list[0]?.id ?? "");
        setSelectedConferenceId(firstId);
        setAddForm((p) => ({ ...p, conference_id: firstId }));
      }
    } catch (e) {
      const status = e?.response?.status;
      setErr(
        e?.response?.data?.message ||
          e?.message ||
          `Conferences load failed (${status || "unknown"})`
      );
      setConferences([]);
    } finally {
      setConfLoading(false);
    }
  };

  const loadSponsorLogosByConference = async (conferenceId) => {
    if (!conferenceId) {
      setRows([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErr("");

      const res = await axios.get(LOGOS_BY_CONF_URL(conferenceId), {
        headers: apiHeaders(),
      });

      const list = normalizeArray(res.data);
      setRows(list);
    } catch (e) {
      const status = e?.response?.status;
      setErr(
        e?.response?.data?.message ||
          e?.message ||
          `Sponsor logos load failed (${status || "unknown"})`
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshCurrentConference = async () => {
    await loadSponsorLogosByConference(selectedConferenceId);
  };

  const openAddModal = () => {
    setAddForm({
      conference_id: selectedConferenceId || "",
      sponsor_title: "",
      sponsor_logo: null,
    });
    setAddModal(true);
  };

  const closeAddModal = () => {
    setAddModal(false);
    setAddForm({
      conference_id: selectedConferenceId || "",
      sponsor_title: "",
      sponsor_logo: null,
    });
  };

  const openViewModal = (item) => {
    setSelectedLogo(item);
    setViewModal(true);
  };

  const closeViewModal = () => {
    setViewModal(false);
    setSelectedLogo(null);
  };

  const openEditModal = (item) => {
    setSelectedLogo(item);
    setEditForm({
      id: String(item?.id ?? ""),
      conference_id: String(item?.conference_id ?? selectedConferenceId ?? ""),
      sponsor_title: String(item?.sponsor_title ?? ""),
      sponsor_logo: null,
      preview: getImageUrl(item),
    });
    setEditModal(true);
  };

  const closeEditModal = () => {
    setEditModal(false);
    setSelectedLogo(null);
    setEditForm({
      id: "",
      conference_id: "",
      sponsor_title: "",
      sponsor_logo: null,
      preview: "",
    });
  };

  const onAddChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "sponsor_logo") {
      setAddForm((p) => ({ ...p, sponsor_logo: files?.[0] || null }));
    } else {
      setAddForm((p) => ({ ...p, [name]: value }));
    }
  };

  const onEditChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "sponsor_logo") {
      const file = files?.[0] || null;
      setEditForm((p) => ({
        ...p,
        sponsor_logo: file,
        preview: file ? URL.createObjectURL(file) : p.preview,
      }));
    } else {
      setEditForm((p) => ({ ...p, [name]: value }));
    }
  };

  const handleAdd = async () => {
    try {
      setAdding(true);
      setErr("");

      if (!addForm.conference_id) {
        throw new Error("Conference is required");
      }

      // if (!addForm.sponsor_title.trim()) {
      //   throw new Error("Sponsor title is required");
      // }

      if (!addForm.sponsor_logo) {
        throw new Error("Sponsor logo file is required");
      }

      const formData = new FormData();
      formData.append("conference_id", addForm.conference_id);
      formData.append("sponsor_title", addForm.sponsor_title.trim());
      formData.append("sponsor_logo", addForm.sponsor_logo);

      await axios.post(ADD_URL, formData, {
        headers: apiHeaders(),
      });

      setAddModal(false);

      if (String(addForm.conference_id) !== String(selectedConferenceId)) {
        setSelectedConferenceId(String(addForm.conference_id));
      } else {
        await refreshCurrentConference();
      }
    } catch (e) {
      const status = e?.response?.status;
      setErr(
        e?.response?.data?.message ||
          e?.message ||
          `Add failed (${status || "unknown"})`
      );
    } finally {
      setAdding(false);
    }
  };

  const handleUpdate = async () => {
    if (!editForm.id) return;

    try {
      setSaving(true);
      setErr("");

      if (!editForm.conference_id) {
        throw new Error("Conference is required");
      }

      // if (!editForm.sponsor_title.trim()) {
      //   throw new Error("Sponsor title is required");
      // }

      const formData = new FormData();
      formData.append("conference_id", editForm.conference_id);
      formData.append("sponsor_title", editForm.sponsor_title.trim());

      if (editForm.sponsor_logo) {
        formData.append("sponsor_logo", editForm.sponsor_logo);
      }

      try {
        await axios.post(EDIT_URL(editForm.id), formData, {
          headers: apiHeaders(),
        });
      } catch {
        await axios.post(EDIT_URL(editForm.id), formData, {
          headers: apiHeaders(),
        });
      }

      setEditModal(false);

      if (String(editForm.conference_id) !== String(selectedConferenceId)) {
        setSelectedConferenceId(String(editForm.conference_id));
      } else {
        await refreshCurrentConference();
      }
    } catch (e) {
      const status = e?.response?.status;
      setErr(
        e?.response?.data?.message ||
          e?.message ||
          `Update failed (${status || "unknown"})`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (id) => {
    setConfirmId(id);
    setShowConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmId) return;

    try {
      setDeletingId(confirmId);
      setErr("");

      await axios.delete(DELETE_URL(confirmId), {
        headers: apiHeaders(),
      });

      setRows((prev) => prev.filter((item) => String(item.id) !== String(confirmId)));

      if (viewModal && String(selectedLogo?.id) === String(confirmId)) {
        closeViewModal();
      }

      if (editModal && String(selectedLogo?.id) === String(confirmId)) {
        closeEditModal();
      }

      await refreshCurrentConference();
    } catch (e) {
      const status = e?.response?.status;
      setErr(
        e?.response?.data?.message ||
          e?.message ||
          `Delete failed (${status || "unknown"})`
      );
    } finally {
      setDeletingId(null);
      setShowConfirm(false);
      setConfirmId(null);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirm(false);
    setConfirmId(null);
  };

  useEffect(() => {
    loadConferences();
  }, []);

  useEffect(() => {
    if (selectedConferenceId) {
      loadSponsorLogosByConference(selectedConferenceId);
      setAddForm((p) => ({ ...p, conference_id: selectedConferenceId }));
    }
  }, [selectedConferenceId]);

  if (loading && rows.length === 0) {
    return <div style={{ padding: 16, fontWeight: 400 }}>Loading sponsor logos...</div>;
  }

  return (
    <>
      <div style={{ padding: 16, fontWeight: 400 }}>
        <h2 style={{ fontWeight: 400, margin: 0 }}>
          Sponsor Logos ({rows.length})
        </h2>

        <div
          style={{
            marginTop: 12,
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 13, color: "#475569" }}>Conference</div>

          <select
            value={selectedConferenceId}
            onChange={(e) => setSelectedConferenceId(e.target.value)}
            disabled={confLoading || conferences.length === 0}
            style={selectStyle({ minWidth: 260 })}
          >
            {confLoading && <option value="">Loading conferences...</option>}
            {!confLoading && conferences.length === 0 && (
              <option value="">No conferences found</option>
            )}
            {!confLoading &&
              conferences.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name || c.title || c.conference_name || `Conference #${c.id}`}
                </option>
              ))}
          </select>

          <button
            onClick={() => loadSponsorLogosByConference(selectedConferenceId)}
            disabled={!selectedConferenceId || loading}
            style={btnPrimary}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>

          <button onClick={loadConferences} disabled={confLoading} style={btnGray}>
            {confLoading ? "Refreshing..." : "Refresh Conferences"}
          </button>

          <button
            onClick={openAddModal}
            disabled={!selectedConferenceId}
            style={btnGreen}
          >
            Add Sponsor Logo
          </button>
        </div>

        {err && <div style={errBox}>{err}</div>}

        <div style={tableWrap}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <Th>ID</Th>
                <Th>Conference ID</Th>
                <Th>Title</Th>
                <Th>Preview</Th>
                {/* <Th>Created At</Th>
                <Th>Updated At</Th> */}
                <Th>Actions</Th>
              </tr>
            </thead>

            <tbody>
              {rows.map((item) => {
                const img = getImageUrl(item);

                return (
                  <tr key={item.id}>
                    <Td>{item.id ?? "-"}</Td>
                    <Td>{item.conference_id ?? selectedConferenceId ?? "-"}</Td>
                    <Td>{item.sponsor_title ?? "-"}</Td>
                    <Td>
                      {img ? (
                        <img
                          src={`https://eventscons.com/eventbackendnew/storage/app/${img}`}
                          alt={item.sponsor_title || "Sponsor Logo"}
                          style={{
                            width: 70,
                            height: 45,
                            objectFit: "contain",
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            padding: 4,
                            background: "#fff",
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </Td>
                    {/* <Td>{fmtDate(item.created_at)}</Td>
                    <Td>{fmtDate(item.updated_at)}</Td> */}
                    <Td style={{ whiteSpace: "nowrap" }}>
                      <button onClick={() => openViewModal(item)} style={btnView(false)}>
                        View
                      </button>

                      <button onClick={() => openEditModal(item)} style={btnEdit(false)}>
                        Edit
                      </button>

                      <button
                        onClick={() => handleDeleteClick(item.id)}
                        disabled={deletingId === item.id}
                        style={btnDelete(deletingId === item.id)}
                      >
                        {deletingId === item.id ? "Deleting..." : "Delete"}
                      </button>
                    </Td>
                  </tr>
                );
              })}

              {rows.length === 0 && (
                <tr>
                  <Td colSpan={7} style={{ textAlign: "center", color: "#64748b" }}>
                    No sponsor logos found
                  </Td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showConfirm && (
        <Overlay onClose={handleCancelDelete}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 16px 0", color: "#1f2937", fontWeight: 400 }}>
              Delete Sponsor Logo?
            </h3>
            <p style={{ margin: "0 0 24px 0", color: "#6b7280", fontSize: 14, fontWeight: 400 }}>
              Are you sure you want to delete this sponsor logo? This action cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={handleCancelDelete} style={btnGray}>
                Cancel
              </button>
              <button onClick={handleConfirmDelete} disabled={deletingId !== null} style={btnRed}>
                {deletingId === confirmId ? "Deleting..." : "Delete"}
              </button>
            </div>
          </ModalBox>
        </Overlay>
      )}

      {viewModal && selectedLogo && (
        <Overlay onClose={closeViewModal}>
          <ModalBoxWide onClick={(e) => e.stopPropagation()}>
            <ModalHeader
              title={`Sponsor Logo #${selectedLogo.id}`}
              onClose={closeViewModal}
            />

            <div style={grid2}>
              <DetailItem label="ID" value={selectedLogo.id ?? "-"} />
              <DetailItem label="Conference ID" value={selectedLogo.conference_id ?? "-"} />
              <DetailItem label="Sponsor Title" value={selectedLogo.sponsor_title ?? "-"} />
              <DetailItem label="Created At" value={fmtDate(selectedLogo.created_at)} />
              <DetailItem label="Updated At" value={fmtDate(selectedLogo.updated_at)} />
            </div>

            {getImageUrl(selectedLogo) && (
              <div style={{ marginTop: 18 }}>
                <div style={fieldLabelStyle}>Preview</div>
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 16,
                    background: "#f8fafc",
                  }}
                >
                  <img
                    src={`https://eventscons.com/eventbackendnew/storage/app/${getImageUrl(selectedLogo)}`}
                    alt={selectedLogo.sponsor_title || "Sponsor Logo"}
                    style={{
                      maxWidth: "100%",
                      maxHeight: 260,
                      objectFit: "contain",
                      display: "block",
                      margin: "0 auto",
                    }}
                  />
                </div>
              </div>
            )}
          </ModalBoxWide>
        </Overlay>
      )}

      {addModal && (
        <Overlay onClose={closeAddModal}>
          <ModalBoxWide onClick={(e) => e.stopPropagation()}>
            <ModalHeader title="Add Sponsor Logo" onClose={closeAddModal} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Field label="Conference">
                <select
                  name="conference_id"
                  value={addForm.conference_id}
                  onChange={onAddChange}
                  style={selectStyle()}
                >
                  <option value="">Select</option>
                  {conferences.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name || c.title || c.conference_name || `Conference #${c.id}`}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Sponsor Title (Optional)">
                <Input
                  name="sponsor_title"
                  value={addForm.sponsor_title}
                  onChange={onAddChange}
                  placeholder="Enter sponsor title"
                />
              </Field>

              <Field label="Sponsor Logo">
                <input
                  type="file"
                  name="sponsor_logo"
                  accept="image/*"
                  onChange={onAddChange}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #e5e7eb",
                    outline: "none",
                    fontSize: 14,
                    fontWeight: 400,
                    background: "white",
                  }}
                />
              </Field>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
              <button onClick={closeAddModal} disabled={adding} style={btnGray}>
                Cancel
              </button>
              <button onClick={handleAdd} disabled={adding} style={btnGreen}>
                {adding ? "Saving..." : "Add"}
              </button>
            </div>
          </ModalBoxWide>
        </Overlay>
      )}

      {editModal && selectedLogo && (
        <Overlay onClose={closeEditModal}>
          <ModalBoxWide onClick={(e) => e.stopPropagation()}>
            <ModalHeader title={`Edit Sponsor Logo #${selectedLogo.id}`} onClose={closeEditModal} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Field label="Conference">
                <select
                  name="conference_id"
                  value={editForm.conference_id}
                  onChange={onEditChange}
                  style={selectStyle()}
                >
                  <option value="">Select</option>
                  {conferences.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name || c.title || c.conference_name || `Conference #${c.id}`}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Sponsor Title">
                <Input
                  name="sponsor_title"
                  value={editForm.sponsor_title}
                  onChange={onEditChange}
                  placeholder="Enter sponsor title"
                />
              </Field>

              <Field label="Replace Logo">
                <input
                  type="file"
                  name="sponsor_logo"
                  accept="image/*"
                  onChange={onEditChange}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #e5e7eb",
                    outline: "none",
                    fontSize: 14,
                    fontWeight: 400,
                    background: "white",
                  }}
                />
              </Field>
            </div>

            {editForm.preview && (
              <div style={{ marginTop: 18 }}>
                <div style={fieldLabelStyle}>Preview</div>
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 16,
                    background: "#f8fafc",
                  }}
                >
                  <img
                    src={`https://eventscons.com/eventbackendnew/storage/app/${editForm.preview}`}
                    alt={editForm.sponsor_title || "Preview"}
                    style={{
                      maxWidth: "100%",
                      maxHeight: 260,
                      objectFit: "contain",
                      display: "block",
                      margin: "0 auto",
                    }}
                  />
                </div>
              </div>
            )}

            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() => handleDeleteClick(selectedLogo.id)}
                style={btnRed}
                disabled={deletingId === selectedLogo.id}
              >
                {deletingId === selectedLogo.id ? "Deleting..." : "Delete"}
              </button>

              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={closeEditModal} disabled={saving} style={btnGray}>
                  Cancel
                </button>
                <button onClick={handleUpdate} disabled={saving} style={btnGreen}>
                  {saving ? "Updating..." : "Update"}
                </button>
              </div>
            </div>
          </ModalBoxWide>
        </Overlay>
      )}
    </>
  );
}

function fmtDate(v) {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return "-";
  }
}

function Th({ children }) {
  return <th style={thStyle}>{children}</th>;
}

function Td({ children, ...rest }) {
  return (
    <td style={tdStyle} {...rest}>
      {children}
    </td>
  );
}

function DetailItem({ label, value }) {
  return (
    <div style={{ fontWeight: 400 }}>
      <div style={detailLabel}>{label}</div>
      <div style={detailValue}>{String(value)}</div>
    </div>
  );
}

function Overlay({ children, onClose }) {
  return (
    <div style={overlayStyle} onClick={onClose}>
      {children}
    </div>
  );
}

function ModalHeader({ title, onClose }) {
  return (
    <div style={modalHeaderStyle}>
      <h3 style={{ margin: 0, fontSize: 22, fontWeight: 400, color: "#1f2937" }}>{title}</h3>
      <button onClick={onClose} style={closeBtnStyle}>
        ✕
      </button>
    </div>
  );
}

function ModalBox({ children, ...rest }) {
  return (
    <div style={modalBoxStyle} {...rest}>
      {children}
    </div>
  );
}

function ModalBoxWide({ children, ...rest }) {
  return (
    <div style={modalBoxWideStyle} {...rest}>
      {children}
    </div>
  );
}

const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 };

function Field({ label, children }) {
  return (
    <div style={{ fontWeight: 400 }}>
      <div style={fieldLabelStyle}>{label}</div>
      {children}
    </div>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      style={{
        width: "100%",
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid #e5e7eb",
        outline: "none",
        fontSize: 14,
        fontWeight: 400,
      }}
    />
  );
}

const errBox = {
  marginTop: 12,
  marginBottom: 12,
  padding: 12,
  borderRadius: 8,
  background: "#fef2f2",
  color: "#b91c1c",
  whiteSpace: "pre-wrap",
  fontWeight: 400,
};

const tableWrap = {
  marginTop: 12,
  overflowX: "auto",
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  background: "#fff",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: 1100,
  fontWeight: 400,
};

const thStyle = {
  textAlign: "left",
  padding: 12,
  background: "#f8fafc",
  borderBottom: "2px solid #e5e7eb",
  fontSize: 12,
  color: "#64748b",
  fontWeight: 400,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: 12,
  borderBottom: "1px solid #f1f5f9",
  fontSize: 14,
  whiteSpace: "nowrap",
  fontWeight: 400,
};

const detailLabel = {
  fontSize: 12,
  color: "#6b7280",
  fontWeight: 400,
  marginBottom: 6,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const detailValue = {
  fontSize: 15,
  fontWeight: 400,
  color: "#111827",
  padding: "10px 12px",
  background: "#f8fafc",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  wordBreak: "break-word",
  whiteSpace: "pre-wrap",
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  padding: 20,
  fontWeight: 400,
};

const modalHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 18,
  paddingBottom: 12,
  borderBottom: "1px solid #e5e7eb",
  fontWeight: 400,
};

const closeBtnStyle = {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #d1d5db",
  background: "white",
  cursor: "pointer",
  fontWeight: 400,
};

const modalBoxStyle = {
  background: "white",
  borderRadius: 12,
  padding: 24,
  width: "100%",
  maxWidth: 420,
  boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
  fontWeight: 400,
};

const modalBoxWideStyle = {
  background: "white",
  borderRadius: 12,
  padding: 24,
  width: "100%",
  maxWidth: 900,
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
  fontWeight: 400,
};

const fieldLabelStyle = {
  fontSize: 12,
  color: "#6b7280",
  fontWeight: 400,
  marginBottom: 6,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const selectStyle = (extra = {}) => ({
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  outline: "none",
  fontSize: 14,
  background: "white",
  fontWeight: 400,
  ...extra,
});

const btnGray = {
  padding: "8px 14px",
  borderRadius: 8,
  border: "1px solid #d1d5db",
  background: "white",
  cursor: "pointer",
  fontWeight: 400,
};

const btnPrimary = {
  padding: "8px 14px",
  borderRadius: 8,
  border: "none",
  background: "#9B1321",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 400,
};

const btnRed = {
  padding: "8px 14px",
  borderRadius: 8,
  border: "none",
  background: "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
  color: "white",
  cursor: "pointer",
  fontWeight: 400,
};

const btnGreen = {
  padding: "8px 14px",
  borderRadius: 8,
  border: "none",
  background: "linear-gradient(135deg, #16a34a 0%, #16a34a 100%)",
  color: "white",
  cursor: "pointer",
  fontWeight: 400,
};

const btnView = (disabled) => ({
  padding: "6px 12px",
  marginRight: 8,
  borderRadius: 6,
  border: "1px solid #3b82f6",
  background: disabled ? "#e5e7eb" : "#dbeafe",
  color: "#1d4ed8",
  fontSize: 12,
  fontWeight: 400,
  cursor: disabled ? "not-allowed" : "pointer",
});

const btnEdit = (disabled) => ({
  padding: "6px 12px",
  marginRight: 8,
  borderRadius: 6,
  border: "1px solid #a855f7",
  background: disabled ? "#e5e7eb" : "#f3e8ff",
  color: "#6b21a8",
  fontSize: 12,
  fontWeight: 400,
  cursor: disabled ? "not-allowed" : "pointer",
});

const btnDelete = (disabled) => ({
  padding: "6px 12px",
  borderRadius: 6,
  border: "none",
  background: disabled ? "#9ca3af" : "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
  color: "white",
  fontSize: 12,
  fontWeight: 400,
  cursor: disabled ? "not-allowed" : "pointer",
});